import {useState, useRef, useEffect, useLayoutEffect, useContext} from 'react';
import classNames from "classnames";
import ActionCallout from "../../components/action-callout";
import BidCountdown from "../product-card/components/bid-countdown";
import BidModal from "./components/bid-modal";
import {AUCTIONSTATUSENUM, getStatus} from "./index";
import Currency from "@autocx/forms/src/controls/currency";
import {WebsiteContext} from "../../index";
import PublicBidHistoryModal from "./components/public-bid-history-modal";
import useAuctionConnection, {isAuctionConnectionInterrupted} from '../../realtime/use-auction-connection';
import {
    OPEN_AUCTION_BID_STATUS,
    getAuctionBidCtaLabel,
    getAuctionOpenBidHelperText,
    getAuctionOpenBidStatusLabel,
    getAuctionReserveMetLabel,
    getOpenAuctionBidStatus,
    hasBidAmount
} from "./bid-status";

const OPEN_ACTION_EXIT_MS = 180;
const RESERVE_TO_CONFIRMING_TONE_MS = 140;
const RESERVE_TO_CONFIRMING_RESIZE_MS = 180;
const STATUS_BANNER_CONTENT_EXIT_MS = 140;
const STATUS_BANNER_CONTENT_ENTER_MS = 180;
const STATUS_BANNER_ANIMATION_FALLBACK_BUFFER_MS = 80;

const STATUS_BANNER_TRANSITION_STAGE = {
    IDLE: 'idle',
    TONE: 'tone',
    RESIZE: 'resize',
    CONTENT_OUT: 'content-out',
    CONTENT_IN: 'content-in'
};

const getAuctionStatusBannerSignature = (banner) => [
    banner?.key ?? '',
    banner?.phase ?? '',
    banner?.title ?? '',
    banner?.description ?? '',
    banner?.tone ?? '',
    banner?.compact ? 'compact' : 'expanded'
].join('|');

const prefersReducedMotion = () => typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const getSalesEventLotKey = (event) => String(event?.lotId ?? '');
const normaliseLotOutcome = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
const getSalesEventEndTime = (event) => {
    const endTime = event?.endDate ? new Date(event.endDate).getTime() : NaN;
    return Number.isFinite(endTime) ? endTime : null;
};

function useAuctionStatusBannerTransition(targetBanner) {
    const createIdlePresentation = (banner) => ({
        banner,
        stage: STATUS_BANNER_TRANSITION_STAGE.IDLE,
        shellExpanded: Boolean(banner && !banner.compact),
        reserveNeutralTransition: false
    });
    const [presentation, setPresentation] = useState(() => ({
        ...createIdlePresentation(targetBanner)
    }));
    const presentationRef = useRef(presentation);
    const desiredBannerRef = useRef(targetBanner);
    const targetSignature = getAuctionStatusBannerSignature(targetBanner);
    const timeoutRefs = useRef([]);
    const transitionActiveRef = useRef(false);

    const clearTransitionTimeouts = () => {
        timeoutRefs.current.forEach(clearTimeout);
        timeoutRefs.current = [];
    };

    const updatePresentation = (nextPresentation) => {
        presentationRef.current = nextPresentation;
        setPresentation(nextPresentation);
    };

    const finishContentEntrance = () => {
        const displayedBanner = presentationRef.current.banner;
        updatePresentation(createIdlePresentation(displayedBanner));
        transitionActiveRef.current = false;

        if (getAuctionStatusBannerSignature(desiredBannerRef.current)
            !== getAuctionStatusBannerSignature(displayedBanner)) {
            beginTransition();
        }
    };

    const enterDesiredBanner = () => {
        const desiredBanner = desiredBannerRef.current;

        if (!desiredBanner) {
            updatePresentation(createIdlePresentation(null));
            transitionActiveRef.current = false;
            return;
        }

        updatePresentation({
            banner: desiredBanner,
            stage: STATUS_BANNER_TRANSITION_STAGE.CONTENT_IN,
            shellExpanded: !desiredBanner.compact,
            reserveNeutralTransition: false
        });
        timeoutRefs.current.push(setTimeout(
            finishContentEntrance,
            STATUS_BANNER_CONTENT_ENTER_MS + STATUS_BANNER_ANIMATION_FALLBACK_BUFFER_MS
        ));
    };

    const exitCurrentContent = () => {
        updatePresentation({
            ...presentationRef.current,
            stage: STATUS_BANNER_TRANSITION_STAGE.CONTENT_OUT
        });
        timeoutRefs.current.push(setTimeout(
            enterDesiredBanner,
            STATUS_BANNER_CONTENT_EXIT_MS + STATUS_BANNER_ANIMATION_FALLBACK_BUFFER_MS
        ));
    };

    const resizeReserveBanner = () => {
        const currentBanner = presentationRef.current.banner;
        const desiredBanner = desiredBannerRef.current;

        if (getAuctionStatusBannerSignature(currentBanner)
            === getAuctionStatusBannerSignature(desiredBanner)) {
            updatePresentation(createIdlePresentation(desiredBanner));
            transitionActiveRef.current = false;
            return;
        }

        updatePresentation({
            banner: currentBanner,
            stage: STATUS_BANNER_TRANSITION_STAGE.RESIZE,
            shellExpanded: true,
            reserveNeutralTransition: presentationRef.current.reserveNeutralTransition
        });
        timeoutRefs.current.push(setTimeout(() => {
            exitCurrentContent();
        }, RESERVE_TO_CONFIRMING_RESIZE_MS));
    };

    function beginTransition() {
        clearTransitionTimeouts();

        const currentBanner = presentationRef.current.banner;
        const desiredBanner = desiredBannerRef.current;
        const currentSignature = getAuctionStatusBannerSignature(currentBanner);
        const desiredSignature = getAuctionStatusBannerSignature(desiredBanner);

        if (currentSignature === desiredSignature) {
            updatePresentation(createIdlePresentation(desiredBanner));
            transitionActiveRef.current = false;
            return;
        }

        if (prefersReducedMotion()) {
            updatePresentation(createIdlePresentation(desiredBanner));
            transitionActiveRef.current = false;
            return;
        }

        transitionActiveRef.current = true;

        if (!currentBanner) {
            enterDesiredBanner();
            return;
        }

        const isLeavingReserveBanner = currentBanner.phase === 'reserve-met'
            && desiredBanner?.phase !== 'reserve-met';

        if (!isLeavingReserveBanner) {
            exitCurrentContent();
            return;
        }

        if (desiredBanner?.tone !== 'neutral') {
            resizeReserveBanner();
            return;
        }

        updatePresentation({
            banner: currentBanner,
            stage: STATUS_BANNER_TRANSITION_STAGE.TONE,
            shellExpanded: false,
            reserveNeutralTransition: true
        });

        timeoutRefs.current.push(setTimeout(() => {
            resizeReserveBanner();
        }, RESERVE_TO_CONFIRMING_TONE_MS));
    }

    useIsomorphicLayoutEffect(() => {
        desiredBannerRef.current = targetBanner;

        if (prefersReducedMotion()) {
            clearTransitionTimeouts();
            transitionActiveRef.current = false;
            updatePresentation(createIdlePresentation(targetBanner));
            return;
        }

        if (!transitionActiveRef.current) {
            beginTransition();
            return;
        }

        const currentPresentation = presentationRef.current;
        const desiredMatchesDisplayed = getAuctionStatusBannerSignature(targetBanner)
            === getAuctionStatusBannerSignature(currentPresentation.banner);
        const canCancelBeforeContentExit = currentPresentation.stage === STATUS_BANNER_TRANSITION_STAGE.TONE
            || currentPresentation.stage === STATUS_BANNER_TRANSITION_STAGE.RESIZE;

        if (desiredMatchesDisplayed && canCancelBeforeContentExit) {
            clearTransitionTimeouts();
            transitionActiveRef.current = false;
            updatePresentation(createIdlePresentation(targetBanner));
        }
    }, [targetSignature]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const settleForReducedMotion = (event) => {
            if (!event.matches) return;

            clearTransitionTimeouts();
            transitionActiveRef.current = false;
            updatePresentation(createIdlePresentation(desiredBannerRef.current));
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', settleForReducedMotion);
        } else {
            mediaQuery.addListener?.(settleForReducedMotion);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', settleForReducedMotion);
            } else {
                mediaQuery.removeListener?.(settleForReducedMotion);
            }
        };
    }, []);

    useEffect(() => () => {
        clearTransitionTimeouts();
        transitionActiveRef.current = false;
    }, []);

    const onContentAnimationEnd = (event) => {
        if (event.target !== event.currentTarget || !transitionActiveRef.current) return;

        const stage = presentationRef.current.stage;
        if (
            stage === STATUS_BANNER_TRANSITION_STAGE.CONTENT_OUT
            && event.animationName === 'auction-status-banner-content-exit'
        ) {
            clearTransitionTimeouts();
            enterDesiredBanner();
        } else if (
            stage === STATUS_BANNER_TRANSITION_STAGE.CONTENT_IN
            && event.animationName === 'auction-status-banner-content-enter'
        ) {
            clearTransitionTimeouts();
            finishContentEntrance();
        }
    };

    return {...presentation, onContentAnimationEnd};
}

function AuctionBidCount({label, interactive = false, onClick, ariaLabel}) {
    if (interactive) {
        return (
            <button
                type="button"
                className="inline rounded-none bg-transparent p-0 text-white underline decoration-white/60 underline-offset-[4px] shadow-none transition hover:decoration-white focus:outline-none"
                onClick={onClick}
                aria-label={ariaLabel}
                style={{
                    appearance: 'none',
                    backgroundColor: 'transparent',
                    border: 0,
                    borderRadius: 0,
                    boxShadow: 'none',
                    color: 'inherit',
                    float: 'none',
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    padding: 0
                }}
            >
                {label}
            </button>
        );
    }

    return <span>{label}</span>;
}

const getCurrentProductReturnUrl = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

function BidSalesEvent({
    productUuid,
    salesEvent,
    onBidSuccess,
    onRefreshRequested,
    usersBid,
    bidButtonClasses,
    profile,
    publicBidHistory,
    isPublicBidHistoryLoading,
    showPublicBidHistory,
    onBidHistoryRequested,
    isBidderOutcomeReady = true,
    variant = 'full'
}) {
    const context = useContext(WebsiteContext);
    const [offerBidModalOpen, setOfferBidModalOpen] = useState(false);
    const [bidHistoryModalOpen, setBidHistoryModalOpen] = useState(false);
    const [openActionExiting, setOpenActionExiting] = useState(false);
    const startDate = salesEvent?.startDate;
    const endDate = salesEvent?.endDate;
    const [secondsRemaining, setSecondsRemaining] = useState(salesEvent?.secondsRemaining);
    const [secondsToStart, setSecondsToStart] = useState(salesEvent?.secondsToStart);

    const [status, setStatus] = useState(() => getStatus(salesEvent, startDate, endDate, secondsToStart, secondsRemaining));
    const [optimisticTotalBids, setOptimisticTotalBids] = useState(null);
    const [optimisticHighestBid, setOptimisticHighestBid] = useState(null);
    const [optimisticUsersBid, setOptimisticUsersBid] = useState(null);
    const [optimisticSalesEvent, setOptimisticSalesEvent] = useState(null);

    const nowRef = useRef(Date.now());
    const [now, setNow] = useState(nowRef.current);
    const openActionExitTimeoutRef = useRef(null);
    const onRefreshRequestedRef = useRef(onRefreshRequested);
    const completedCountdownRef = useRef(null);
    // A settled lot opened from history already has a public result. Loading the
    // viewer's identity must not make that result look undecided again. Preserve
    // the existing confirming transition for lots watched through their close.
    const entryResultRef = useRef(null);
    const entryLotKey = getSalesEventLotKey(salesEvent);
    if (entryResultRef.current?.lotKey !== entryLotKey) {
        entryResultRef.current = {
            lotKey: entryLotKey,
            lotStatus: normaliseLotOutcome(salesEvent?.lotStatus)
        };
    }
    const isAuction = salesEvent?.sellingMethod?.toLowerCase() === 'auction';
    const isCompactAuction = variant === 'compact' && isAuction;
    const authoritativeTotalBids = salesEvent?.totalBids ?? 0;
    const authoritativeHighestBid = salesEvent?.highestBid ?? 0;
    const authoritativeUsersBidAmount = hasBidAmount(usersBid) ? Number(usersBid.amount) : null;
    const optimisticUsersBidAmount = hasBidAmount(optimisticUsersBid) ? Number(optimisticUsersBid.amount) : null;
    const shouldUseOptimisticUsersBid = Number.isFinite(optimisticUsersBidAmount)
        && (!Number.isFinite(authoritativeUsersBidAmount) || authoritativeUsersBidAmount < optimisticUsersBidAmount);
    const authoritativeIsFinal = ['sold', 'passedin', 'salefellthrough', 'withdrawn', 'cancelled', 'canceled']
        .includes(normaliseLotOutcome(salesEvent?.lotStatus));
    const optimisticIsCurrent = optimisticSalesEvent && !authoritativeIsFinal
        && Number(salesEvent?.totalBids ?? 0) < Number(optimisticSalesEvent?.totalBids ?? optimisticTotalBids ?? 0);
    const effectiveSalesEvent = optimisticIsCurrent
        ? {...salesEvent, ...optimisticSalesEvent}
        : salesEvent;
    const effectiveUsersBid = shouldUseOptimisticUsersBid
        ? {...usersBid, ...optimisticUsersBid}
        : usersBid;
    const usersBidAmount = hasBidAmount(effectiveUsersBid) ? Number(effectiveUsersBid.amount) : 0;
    const lotOutcome = normaliseLotOutcome(effectiveSalesEvent?.lotStatus);
    const isPassedIn = lotOutcome === 'passedin';
    const isSaleFellThrough = lotOutcome === 'salefellthrough';
    const hasFinalNoWinnerOutcome = isPassedIn || isSaleFellThrough;
    const effectiveStatus = hasFinalNoWinnerOutcome ? AUCTIONSTATUSENUM.ENDED : status;
    const effectiveSecondsToStart = secondsToStart;
    const effectiveSecondsRemaining = secondsRemaining;
    const currentHighestBid = Math.max(authoritativeHighestBid, optimisticHighestBid ?? 0, usersBidAmount);
    const highestBidAmount = currentHighestBid;
    const totalBids = Math.max(authoritativeTotalBids, optimisticTotalBids ?? 0);
    const hasUsersBid = hasBidAmount(effectiveUsersBid);
    const hasKnownBids = totalBids > 0 || highestBidAmount > 0 || hasUsersBid;
    const reserveMet = !!effectiveUsersBid?.reserveMet || (!!effectiveSalesEvent?.reserveMet && hasKnownBids);
    const isUsersBidHighest = hasUsersBid && highestBidAmount > 0 && usersBidAmount >= highestBidAmount;
    const isCurrentUserWinner = !isSaleFellThrough && !!effectiveUsersBid?.isWinner;
    const isSignedIn = !!(profile?.customer && profile?.registered);
    const isWithdrawnOrCancelled = ['withdrawn', 'cancelled', 'canceled'].includes(lotOutcome);
    const isFinalisedStatus = effectiveStatus === AUCTIONSTATUSENUM.ENDED || effectiveStatus === AUCTIONSTATUSENUM.CLOSED;
    const isNotStarted = effectiveStatus === AUCTIONSTATUSENUM.NOT_STARTED;
    const hasResolvedWinner = !hasFinalNoWinnerOutcome && effectiveStatus === AUCTIONSTATUSENUM.CLOSED;
    const hasAuthoritativeOutcome = hasResolvedWinner || hasFinalNoWinnerOutcome || isWithdrawnOrCancelled;
    const connectionState = useAuctionConnection(isAuction && !hasAuthoritativeOutcome
        ? [{productUuid, lotId: salesEvent?.lotId}] : []);
    const connectionInterrupted = isAuction && !hasAuthoritativeOutcome && isAuctionConnectionInterrupted(connectionState);
    const bidSummaryLabel = hasAuthoritativeOutcome ? 'Final Bid' : connectionInterrupted ? 'Last Known Bid' : 'Current Bid';
    const isQuietAuctionEnd = isAuction && isFinalisedStatus && !hasResolvedWinner
        && (isWithdrawnOrCancelled || !hasKnownBids);
    const showKnownPublicResult = !isBidderOutcomeReady
        && (hasResolvedWinner || isPassedIn)
        && entryResultRef.current.lotStatus === lotOutcome;
    const isResolvedWinnerPendingBidderContext = isAuction && !showKnownPublicResult
        && (hasResolvedWinner || (isPassedIn && hasKnownBids)) && !isBidderOutcomeReady;
    const eventEndTime = effectiveSalesEvent?.endDate ? new Date(effectiveSalesEvent.endDate).getTime() : null;
    const isTenderInstantWin = !isAuction && hasResolvedWinner && eventEndTime && eventEndTime > now;
    const hasPendingOutcome = effectiveStatus === AUCTIONSTATUSENUM.ENDED
        && !hasFinalNoWinnerOutcome
        && !isWithdrawnOrCancelled
        && !hasResolvedWinner
        && (!isAuction || hasKnownBids);
    const safePublicBidHistory = Array.isArray(publicBidHistory) ? publicBidHistory : [];
    const historyIsLoading = isPublicBidHistoryLoading;
    const canOpenBidHistory = variant === 'full' && !!(showPublicBidHistory && (historyIsLoading || totalBids > 0 || safePublicBidHistory.length > 0));
    const derivedOpenBidStatus = getOpenAuctionBidStatus({
        isAuction,
        status: effectiveStatus,
        usersBidAmount: effectiveUsersBid?.amount,
        highestBidAmount: currentHighestBid
    });
    const openBidStatus = derivedOpenBidStatus;
    const reserveMetLabel = getAuctionReserveMetLabel(effectiveSalesEvent);
    const openBidStatusLabel = connectionInterrupted ? null : getAuctionOpenBidStatusLabel(openBidStatus, effectiveSalesEvent);
    const openBidHelperText = connectionInterrupted ? null : getAuctionOpenBidHelperText(openBidStatus, effectiveSalesEvent);
    const openBidStatusTone = openBidStatus === OPEN_AUCTION_BID_STATUS.LEADING
        ? 'success'
        : openBidStatus === OPEN_AUCTION_BID_STATUS.OUTBID
            ? 'warning'
            : 'neutral';
    const bidActionLabel = getAuctionBidCtaLabel({
        isAuction,
        hasUsersBid,
        openBidStatus,
        saleMethod: effectiveSalesEvent?.sellingMethod
    });
    const eyebrowAnimationKey = [
        effectiveSalesEvent?.lotId ?? 'lot',
        openBidStatus,
        effectiveUsersBid?.amount ?? 'none',
        currentHighestBid,
        totalBids
    ].join('-');
    const showYourBidStat = isAuction && hasBidAmount(effectiveUsersBid);

    let statusMessage = null;
    if (isSaleFellThrough) {
        statusMessage = "Sale Cancelled";
    } else if (isResolvedWinnerPendingBidderContext) {
        statusMessage = "Confirming Result…";
    } else if (hasResolvedWinner && (!isSignedIn || showKnownPublicResult)) {
        statusMessage = isTenderInstantWin ? "Instantly Won" : "Sold";
    } else if (hasResolvedWinner && isCurrentUserWinner) {
        statusMessage = "You Won!";
    } else if (hasResolvedWinner && !isCurrentUserWinner && hasUsersBid) {
        statusMessage = "You Didn’t Win";
    } else if (hasResolvedWinner && !isCurrentUserWinner && !hasUsersBid) {
        statusMessage = "Someone else won";
    } else if (isQuietAuctionEnd) {
        statusMessage = "Auction Ended";
    } else if (isPassedIn && isUsersBidHighest && !showKnownPublicResult) {
        statusMessage = "Reserve Not Met";
    } else if (isPassedIn && hasUsersBid && !showKnownPublicResult) {
        statusMessage = "You Didn’t Win";
    } else if (isPassedIn) {
        statusMessage = "Reserve Not Met";
    } else if (!isAuction && hasPendingOutcome && effectiveUsersBid?.amount) {
        statusMessage = "We’ll be in contact if you’re successful";
    } else if (hasPendingOutcome) {
        statusMessage = "Confirming Result…";
    }

    let auctionStatusBanner = null;
    if (variant === 'full' && isAuction) {
        if (isSaleFellThrough) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-sale-cancelled`,
                phase: 'sale-cancelled',
                title: 'Sale Cancelled',
                description: 'This sale is no longer proceeding.',
                tone: 'neutral'
            };
        } else if (effectiveStatus === AUCTIONSTATUSENUM.OPEN && reserveMet) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-reserve-met`,
                phase: 'reserve-met',
                title: reserveMetLabel,
                tone: 'success',
                compact: true
            };
        } else if (isResolvedWinnerPendingBidderContext) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-confirming`,
                phase: 'confirming',
                title: 'Confirming Result…',
                description: 'This usually takes a few moments.',
                tone: 'neutral'
            };
        } else if (hasResolvedWinner && isCurrentUserWinner && !showKnownPublicResult) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-won`,
                phase: 'won',
                title: 'You Won!',
                description: "Congratulations — you’re the winning bidder.",
                tone: 'success'
            };
        } else if (hasResolvedWinner && hasUsersBid && !showKnownPublicResult) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-lost`,
                phase: 'lost',
                title: 'You Didn’t Win',
                description: 'Another bidder placed the highest bid.',
                tone: 'neutral'
            };
        } else if (hasResolvedWinner) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-sold`,
                phase: 'sold',
                title: 'Sold',
                description: 'Bidding has ended.',
                tone: 'neutral'
            };
        } else if (isPassedIn && hasKnownBids && isUsersBidHighest && !showKnownPublicResult) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-referred`,
                phase: 'reserve-not-met',
                title: 'Reserve Not Met',
                description: 'Your bid was the highest. The seller may contact you.',
                tone: 'neutral'
            };
        } else if (isPassedIn && hasKnownBids && hasUsersBid && !showKnownPublicResult) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-passed-in-lost`,
                phase: 'lost-reserve-not-met',
                title: 'You Didn’t Win',
                description: 'Another bidder placed the highest bid, but the reserve wasn’t met.',
                tone: 'neutral'
            };
        } else if (isPassedIn && hasKnownBids) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-passed-in`,
                phase: 'reserve-not-met',
                title: 'Reserve Not Met',
                description: 'The auction ended without meeting the reserve.',
                tone: 'neutral'
            };
        } else if (hasPendingOutcome) {
            auctionStatusBanner = {
                key: `${effectiveSalesEvent?.lotId ?? 'lot'}-confirming`,
                phase: 'confirming',
                title: 'Confirming Result…',
                description: 'This usually takes a few moments.',
                tone: 'neutral'
            };
        }
    }

    const auctionStatusBannerPresentation = useAuctionStatusBannerTransition(auctionStatusBanner);
    const displayedAuctionStatusBanner = auctionStatusBannerPresentation.banner;
    const auctionStatusBannerTransitionStage = auctionStatusBannerPresentation.stage;
    const isReserveToConfirmingToneTransition = auctionStatusBannerPresentation.reserveNeutralTransition;
    const isReserveToConfirmingResizeTransition = auctionStatusBannerPresentation.shellExpanded
        && displayedAuctionStatusBanner?.compact;
    const isStatusBannerContentExiting = auctionStatusBannerTransitionStage === STATUS_BANNER_TRANSITION_STAGE.CONTENT_OUT;
    const isStatusBannerContentEntering = auctionStatusBannerTransitionStage === STATUS_BANNER_TRANSITION_STAGE.CONTENT_IN;

    useEffect(() => {
        nowRef.current = Date.now();
        setNow(nowRef.current);
    }, []);

    useEffect(() => {
        onRefreshRequestedRef.current = onRefreshRequested;
    }, [onRefreshRequested]);

    useEffect(() => () => {
        if (openActionExitTimeoutRef.current) clearTimeout(openActionExitTimeoutRef.current);
    }, []);

    useIsomorphicLayoutEffect(() => {
        const incomingLotKey = getSalesEventLotKey(salesEvent);
        const incomingEndTime = getSalesEventEndTime(salesEvent);
        const completedCountdown = completedCountdownRef.current;
        const isSameCompletedLot = completedCountdown?.lotKey === incomingLotKey;
        const hasLaterClosingTime = isSameCompletedLot
            && incomingEndTime !== null
            && completedCountdown.endTime !== null
            && incomingEndTime > completedCountdown.endTime;

        if (!isSameCompletedLot || hasLaterClosingTime) {
            completedCountdownRef.current = null;
        }

        nowRef.current = Date.now();
        setNow(nowRef.current);
        setSecondsRemaining(isSameCompletedLot && !hasLaterClosingTime
            ? 0
            : salesEvent?.secondsRemaining ?? 0);
        setSecondsToStart(salesEvent?.secondsToStart ?? 0);
    }, [salesEvent?.lotId, salesEvent?.endDate, salesEvent?.secondsRemaining, salesEvent?.secondsToStart]);

    useEffect(() => {
        setOptimisticTotalBids(null);
    }, [salesEvent?.lotId]);

    useEffect(() => {
        if (optimisticTotalBids !== null && authoritativeTotalBids >= optimisticTotalBids) {
            setOptimisticTotalBids(null);
        }
    }, [authoritativeTotalBids, optimisticTotalBids]);

    useEffect(() => {
        setOptimisticHighestBid(null);
    }, [salesEvent?.lotId]);

    useEffect(() => {
        if (optimisticHighestBid !== null && authoritativeHighestBid >= optimisticHighestBid) {
            setOptimisticHighestBid(null);
        }
    }, [authoritativeHighestBid, optimisticHighestBid]);

    useEffect(() => {
        setOptimisticUsersBid(null);
        setOptimisticSalesEvent(null);
    }, [salesEvent?.lotId]);

    useEffect(() => {
        if (!Number.isFinite(optimisticUsersBidAmount)) return;

        if (Number.isFinite(authoritativeUsersBidAmount) && authoritativeUsersBidAmount >= optimisticUsersBidAmount) {
            setOptimisticUsersBid(null);
        }
    }, [authoritativeUsersBidAmount, optimisticUsersBidAmount]);

    useIsomorphicLayoutEffect(() => {
        const effectiveLotKey = getSalesEventLotKey(effectiveSalesEvent);
        const effectiveEndTime = getSalesEventEndTime(effectiveSalesEvent);
        const completedCountdown = completedCountdownRef.current;
        const isSameCompletedLot = completedCountdown?.lotKey === effectiveLotKey;
        const hasLaterClosingTime = isSameCompletedLot
            && effectiveEndTime !== null
            && completedCountdown.endTime !== null
            && effectiveEndTime > completedCountdown.endTime;
        const lotStatus = normaliseLotOutcome(effectiveSalesEvent?.lotStatus);

        if (completedCountdown && (!isSameCompletedLot || hasLaterClosingTime)) {
            completedCountdownRef.current = null;
        }

        let nextStatus = getStatus(
            effectiveSalesEvent,
            effectiveSalesEvent?.startDate,
            effectiveSalesEvent?.endDate,
            secondsToStart,
            secondsRemaining
        );

        if (lotStatus === 'passedin' || lotStatus === 'salefellthrough') {
            nextStatus = AUCTIONSTATUSENUM.ENDED;
        } else if (
            isSameCompletedLot
            && !hasLaterClosingTime
            && (nextStatus === AUCTIONSTATUSENUM.OPEN || nextStatus === AUCTIONSTATUSENUM.NOT_STARTED)
        ) {
            nextStatus = AUCTIONSTATUSENUM.ENDED;
        }

        setStatus(nextStatus);
    }, [
        effectiveSalesEvent?.lotId,
        effectiveSalesEvent?.startDate,
        effectiveSalesEvent?.endDate,
        effectiveSalesEvent?.lotStatus,
        secondsToStart,
        secondsRemaining,
        effectiveUsersBid?.isWinner
    ]);

    const [resultDelayed, setResultDelayed] = useState(false);
    const waitingForResult = hasPendingOutcome || isResolvedWinnerPendingBidderContext;
    useEffect(() => {
        setResultDelayed(false);
        if (!waitingForResult) return;
        const timer = setTimeout(() => setResultDelayed(true), 30000);
        return () => clearTimeout(timer);
    }, [waitingForResult, effectiveSalesEvent?.lotId]);

    function handleBidSuccess(bidUpdate) {
        const {expectedTotalBids, ...bid} = bidUpdate ?? {};
        const bidPayload = typeof bidUpdate === 'number' ? {amount: bidUpdate} : bid;
        const rawBidAmount = typeof bidUpdate === 'number' ? bidUpdate : bid?.amount;
        const nextBidAmount = rawBidAmount === null || rawBidAmount === undefined ? null : Number(rawBidAmount);
        const updatedSalesEvent = typeof bidPayload?.salesEvent === 'object' && bidPayload.salesEvent !== null
            ? bidPayload.salesEvent
            : null;

        if (updatedSalesEvent) {
            setOptimisticSalesEvent((previousValue) => ({...previousValue, ...updatedSalesEvent}));

            if (
                updatedSalesEvent.endDate !== undefined
                || updatedSalesEvent.secondsRemaining !== undefined
                || updatedSalesEvent.secondsToStart !== undefined
            ) {
                nowRef.current = Date.now();
                setNow(nowRef.current);
            }

            if (updatedSalesEvent.secondsRemaining !== undefined) {
                setSecondsRemaining(updatedSalesEvent.secondsRemaining ?? 0);
            }

            if (updatedSalesEvent.secondsToStart !== undefined) {
                setSecondsToStart(updatedSalesEvent.secondsToStart ?? 0);
            }

            if (updatedSalesEvent.totalBids !== undefined) {
                setOptimisticTotalBids(updatedSalesEvent.totalBids);
            }

            if (updatedSalesEvent.highestBid !== undefined) {
                setOptimisticHighestBid(updatedSalesEvent.highestBid);
            }
        }

        if (updatedSalesEvent?.totalBids === undefined && Number.isFinite(expectedTotalBids)) {
            setOptimisticTotalBids(expectedTotalBids);
        } else if (updatedSalesEvent?.totalBids === undefined && isAuction && Number.isFinite(nextBidAmount)) {
            setOptimisticTotalBids((previousValue) => Math.max(authoritativeTotalBids, previousValue ?? 0) + 1);
        }

        if (updatedSalesEvent?.highestBid === undefined && isAuction && Number.isFinite(nextBidAmount)) {
            setOptimisticHighestBid((previousValue) => Math.max(previousValue ?? authoritativeHighestBid, nextBidAmount));
        }

        if (Number.isFinite(nextBidAmount)) {
            setOptimisticUsersBid((previousValue) => ({...previousValue, ...bidPayload}));
        }

        const {salesEvent: _salesEventUpdate, ...publicBidPayload} = bidPayload;
        onBidSuccess(publicBidPayload);
    }

    function closeBidModal() {
        setOfferBidModalOpen(false);
    }

    function openBidModal() {
        setOfferBidModalOpen(true);
    }

    function openBidHistoryModal() {
        if (!canOpenBidHistory) return;
        setBidHistoryModalOpen(true);
        if (!historyIsLoading) {
            onBidHistoryRequested?.();
        }
    }

    function closeBidHistoryModal() {
        setBidHistoryModalOpen(false);
    }

    function renderCountdown(compact = false, options = {}) {
        if (effectiveStatus !== AUCTIONSTATUSENUM.NOT_STARTED && effectiveStatus !== AUCTIONSTATUSENUM.OPEN) return null;

        const {
            boldUnderMinutes = null,
            className = null,
            showSeconds = true,
            showSecondsUnderMinutes = null,
            suffix = null
        } = options;
        const isStartingCountdown = effectiveStatus === AUCTIONSTATUSENUM.NOT_STARTED;
        const durationSeconds = isStartingCountdown ? effectiveSecondsToStart : effectiveSecondsRemaining;

        return (
            <BidCountdown
                salesEvent={effectiveSalesEvent}
                date={now + durationSeconds * 1000}
                warningMinutes={1}
                showSeconds={showSeconds}
                showSecondsUnderMinutes={showSecondsUnderMinutes}
                boldUnderMinutes={boldUnderMinutes}
                suffix={suffix}
                completedLabel={null}
                className={classNames(compact ? "whitespace-nowrap" : null, className)}
                key={`${effectiveStatus}-${durationSeconds}`}
                onComplete={() => {
                    if (isStartingCountdown) {
                        nowRef.current = Date.now();
                        setNow(nowRef.current);
                        setSecondsToStart(0);
                        setStatus(AUCTIONSTATUSENUM.OPEN);
                        return;
                    }

                    const prefersReducedMotion = typeof window !== 'undefined'
                        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
                    if (!prefersReducedMotion) {
                        setOpenActionExiting(true);
                        if (openActionExitTimeoutRef.current) clearTimeout(openActionExitTimeoutRef.current);
                        openActionExitTimeoutRef.current = setTimeout(() => {
                            setOpenActionExiting(false);
                            openActionExitTimeoutRef.current = null;
                        }, OPEN_ACTION_EXIT_MS);
                    }
                    completedCountdownRef.current = {
                        lotKey: getSalesEventLotKey(effectiveSalesEvent),
                        endTime: getSalesEventEndTime(effectiveSalesEvent)
                    };
                    setSecondsRemaining(0);
                    setStatus(AUCTIONSTATUSENUM.ENDED);
                }}
            />
        );
    }

    function renderTimeSummary(compact = false) {
        if (effectiveStatus !== AUCTIONSTATUSENUM.NOT_STARTED && effectiveStatus !== AUCTIONSTATUSENUM.OPEN) return null;

        const lineClassName = compact
            ? "flex flex-nowrap items-baseline gap-2 overflow-hidden text-sm font-semibold leading-none text-white"
            : "flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xl font-semibold leading-none text-white";

        if (effectiveStatus === AUCTIONSTATUSENUM.NOT_STARTED) {
            return (
                <div className={lineClassName}>
                    <span className="shrink-0 text-white/75" data-title="">Starts in</span>
                    <div className="min-w-0 truncate">{renderCountdown(compact)}</div>
                </div>
            );
        }

        return (
            <div className={lineClassName}>
                <div className="min-w-0 truncate">{renderCountdown(compact)}</div>
                <span className="shrink-0 text-white/75" data-title="">left</span>
            </div>
        );
    }

    function renderAuctionBidSummary() {
        if (!isAuction) return null;

        const hasHighestBid = highestBidAmount > 0;
        const currentBidValue = hasHighestBid
            ? (
                <Currency.DisplayValue
                    currency={context.currency}
                    country={context.country}
                    value={highestBidAmount}
                    emptyPlaceholder=""
                />
            )
            : isFinalisedStatus
                ? 'No bids received'
                : 'No bids yet';

        const yourBidValue = showYourBidStat
            ? (
                <Currency.DisplayValue
                    currency={context.currency}
                    country={context.country}
                    value={effectiveUsersBid?.amount}
                    emptyPlaceholder=""
                />
            )
            : null;
        const bidCountText = totalBids === 1 ? '1 bid' : `${totalBids} bids`;
        const showOpenBidPrompt = effectiveStatus === AUCTIONSTATUSENUM.OPEN && totalBids === 0;
        const metaBidLabel = showOpenBidPrompt ? 'Place a bid' : bidCountText;
        const showCountdown = effectiveStatus === AUCTIONSTATUSENUM.OPEN;
        const quietStatusMeta = !showCountdown && !auctionStatusBanner ? statusMessage : null;

        return (
            <div className="mb-2 flex flex-col gap-2">
                <div className={classNames(
                    "grid gap-x-6 gap-y-2",
                    showYourBidStat ? "grid-cols-2" : "grid-cols-1"
                )}>
                    <div className="min-w-0">
                        <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-white/55">
                            {bidSummaryLabel}
                        </span>
                        <span className="block truncate text-xl font-semibold leading-tight text-white tabular-nums">
                            {currentBidValue}
                        </span>
                    </div>

                    {showYourBidStat &&
                        <div className="min-w-0">
                            <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-white/55">
                                Your Bid
                            </span>
                            <span className="block truncate text-xl font-semibold leading-tight text-white tabular-nums">
                                {yourBidValue}
                            </span>
                        </div>
                    }
                </div>

                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-tight text-white">
                    {showOpenBidPrompt
                        ? <span className="font-semibold">{metaBidLabel}</span>
                        : <AuctionBidCount
                            label={metaBidLabel}
                            interactive={canOpenBidHistory}
                            onClick={openBidHistoryModal}
                            ariaLabel="View bid history"
                        />
                    }
                    {showCountdown &&
                        <>
                            <span aria-hidden="true">•</span>
                            <span>
                                {renderCountdown(false, {
                                    boldUnderMinutes: 60,
                                    className: "tabular-nums",
                                    showSeconds: false,
                                    showSecondsUnderMinutes: 60,
                                    suffix: ' left'
                                })}
                            </span>
                        </>
                    }
                    {quietStatusMeta &&
                        <>
                            <span aria-hidden="true">•</span>
                            <span>{quietStatusMeta}</span>
                        </>
                    }
                </div>

            </div>
        );
    }

    useEffect(() => {
        if (!canOpenBidHistory) {
            setBidHistoryModalOpen(false);
        }
    }, [canOpenBidHistory, effectiveSalesEvent?.lotId]);

    const fullStatusMessage = isAuction && (effectiveStatus === AUCTIONSTATUSENUM.OPEN || effectiveStatus === AUCTIONSTATUSENUM.NOT_STARTED)
        ? null
        : statusMessage;
    const compactStatusMessage = effectiveStatus === AUCTIONSTATUSENUM.OPEN || effectiveStatus === AUCTIONSTATUSENUM.NOT_STARTED
        ? null
        : statusMessage;
    const renderBidControls = (disabled = false) => {
        disabled = disabled || connectionInterrupted;
        return <>
            <BidModal.BidButton
                saleMethod={effectiveSalesEvent.sellingMethod}
                usersBid={effectiveUsersBid}
                label={isAuction ? bidActionLabel : undefined}
                disabled={disabled}
                openModal={() => {
                    if (disabled) return;
                    if (profile?.customer && profile.registered && profile.customer?._links?.edit) openBidModal();
                    else window.location.href = `/my-account/sign-in?returnUrl=${encodeURIComponent(getCurrentProductReturnUrl())}`;
                }}
                className={bidButtonClasses}
            />
            <BidModal
                isOpen={!disabled && offerBidModalOpen && effectiveStatus === AUCTIONSTATUSENUM.OPEN}
                closeModal={closeBidModal}
                usersBid={usersBid}
                setStatus={setStatus}
                onBidSuccess={handleBidSuccess}
                salesEvent={salesEvent}
                totalBids={totalBids}
                now={now}
            />
        </>;
    };
    const bidButton = effectiveStatus === AUCTIONSTATUSENUM.OPEN ? renderBidControls() : null;

    return (
        <>
            {salesEvent && (
                isCompactAuction
                    ? <div className="flex w-full items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            {renderTimeSummary(true)}
                            {compactStatusMessage &&
                                <div className="mt-1 text-xs font-semibold leading-tight text-white">
                                    {compactStatusMessage}
                                </div>
                            }
                        </div>
                        {effectiveStatus === AUCTIONSTATUSENUM.OPEN &&
                            <div className="shrink-0 w-auto">{bidButton}</div>
                        }
                    </div>
                    : <div className="flex w-full flex-col gap-y-3">
                        {isAuction &&
                            <div
                                className={classNames(
                                    "auction-status-banner-region",
                                    displayedAuctionStatusBanner
                                        ? "auction-panel-full-bleed auction-status-banner-region-enter"
                                        : "auction-status-banner-region--empty"
                                )}
                                data-title=""
                                data-auction-status-shell=""
                                data-auction-status-phase={displayedAuctionStatusBanner?.phase ?? 'open'}
                                aria-hidden={!displayedAuctionStatusBanner}
                            >
                                <div className="auction-status-banner-region__clip">
                                    {displayedAuctionStatusBanner &&
                                    <div
                                        className={classNames(
                                            "auction-reserve-banner auction-status-banner mb-2 grid place-items-center px-4 text-center",
                                            displayedAuctionStatusBanner.compact
                                                ? "auction-status-banner--compact py-2"
                                                : "auction-status-banner--expanded py-3",
                                            displayedAuctionStatusBanner.tone === 'neutral'
                                                ? "auction-status-banner--neutral"
                                                : "auction-status-banner--success",
                                            isReserveToConfirmingToneTransition
                                                ? "auction-status-banner--transitioning-neutral"
                                                : null,
                                            isReserveToConfirmingResizeTransition
                                                ? "auction-status-banner--expanding"
                                                : null
                                        )}
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <div
                                            className={classNames(
                                                "auction-status-banner__content",
                                                isStatusBannerContentExiting
                                                    ? "auction-status-banner__content--exiting"
                                                    : null,
                                                isStatusBannerContentEntering
                                                    ? "auction-status-banner__content--entering"
                                                    : null
                                            )}
                                            onAnimationEnd={auctionStatusBannerPresentation.onContentAnimationEnd}
                                        >
                                            <span className="auction-reserve-banner__text auction-status-banner__title block">
                                                {displayedAuctionStatusBanner.title}
                                                {resultDelayed && waitingForResult && <button type="button" className="underline ml-2"
                                                    onClick={() => onRefreshRequestedRef.current?.(productUuid)}>Retry</button>}
                                            </span>
                                            {displayedAuctionStatusBanner.description &&
                                                <span className="auction-status-banner__description">
                                                    {displayedAuctionStatusBanner.description}
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    }
                                </div>
                            </div>
                        }

                        <div className="flex flex-col gap-y-3" data-status="">
                            {isAuction && !isNotStarted
                                ? renderAuctionBidSummary()
                                : renderTimeSummary()}

                            {fullStatusMessage && !(isAuction && !isNotStarted) &&
                                <div className="text-xl leading-tight">
                                    <span data-title="">{fullStatusMessage}</span>
                                </div>
                            }

                        </div>

                        {isAuction
                            ? (effectiveStatus === AUCTIONSTATUSENUM.OPEN || openActionExiting) &&
                            <div className={classNames(
                                "auction-open-action-region w-full",
                                effectiveStatus !== AUCTIONSTATUSENUM.OPEN && "auction-open-action-region--exit"
                            )}>
                                <div className="auction-open-action-region__clip" aria-hidden={effectiveStatus !== AUCTIONSTATUSENUM.OPEN}>
                                <ActionCallout
                                    eyebrow={isAuction ? openBidStatusLabel : null}
                                    eyebrowKey={isAuction ? eyebrowAnimationKey : null}
                                    eyebrowTone={isAuction ? openBidStatusTone : 'neutral'}
                                    helperText={isAuction ? openBidHelperText : null}
                                >
                                    {renderBidControls(effectiveStatus !== AUCTIONSTATUSENUM.OPEN)}
                                </ActionCallout>
                                </div>
                            </div>
                            : effectiveStatus === AUCTIONSTATUSENUM.OPEN &&
                            <div className="w-full">
                                <ActionCallout>{bidButton}</ActionCallout>
                            </div>
                        }
                    </div>
            )}
            <PublicBidHistoryModal
                isOpen={bidHistoryModalOpen}
                closeModal={closeBidHistoryModal}
                items={safePublicBidHistory}
                isLoading={historyIsLoading}
            />
        </>
    );
}

export default BidSalesEvent;
